/**
 * Referrals Page
 *
 * Displays user's referral program dashboard including:
 * - Unique referral URL with copy button
 * - Stats: clicks, signups, trials, conversions, rewards
 * - List of referrals with status tracking
 * - Reward claiming functionality
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Check,
  Users,
  MousePointer,
  Sparkles,
  Gift,
  Loader2,
  Clock,
  UserPlus,
  CreditCard,
  Trophy,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { SettingsLayout } from '@/client/components/layout/settings-layout';

// =============================================================================
// Types
// =============================================================================

interface ReferralStats {
  totalClicks: number;
  signups: number;
  trialsStarted: number;
  conversions: number;
  rewardsEarned: number;
  rewardsPending: number;
}

interface Referral {
  id: string;
  referredEmail: string;
  status:
    | 'clicked'
    | 'signed_up'
    | 'trial_started'
    | 'converted'
    | 'eligible'
    | 'rewarded'
    | 'expired';
  clickedAt: string | null;
  signedUpAt: string | null;
  trialStartedAt: string | null;
  convertedAt: string | null;
  referrerRewardType: 'discount_25' | 'pro_upgrade' | null;
  referrerRewardAppliedAt: string | null;
}

interface ReferralsData {
  referralCode: string;
  referralUrl: string;
  stats: ReferralStats;
  referrals: Referral[];
}

interface IncomingReferral {
  wasReferred: boolean;
  status?: string;
  rewardEligible?: boolean;
  rewardClaimed?: boolean;
  rewardType?: 'discount_25' | 'pro_upgrade' | null;
}

// =============================================================================
// Status Badge Component
// =============================================================================

function StatusBadge({ status }: { status: Referral['status'] }) {
  const config = {
    clicked: { label: 'Clicked', color: 'bg-zinc-500/20 text-zinc-600', icon: MousePointer },
    signed_up: { label: 'Signed Up', color: 'bg-blue-500/20 text-blue-600', icon: UserPlus },
    trial_started: { label: 'Trial Started', color: 'bg-amber-500/20 text-amber-600', icon: Clock },
    converted: { label: 'Converted', color: 'bg-green-500/20 text-green-600', icon: CreditCard },
    eligible: { label: 'Reward Ready', color: 'bg-purple-500/20 text-purple-600', icon: Gift },
    rewarded: { label: 'Rewarded', color: 'bg-emerald-500/20 text-emerald-600', icon: Trophy },
    expired: { label: 'Expired', color: 'bg-red-500/20 text-red-600', icon: Clock },
  };

  const { label, color, icon: Icon } = config[status] || config.clicked;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        color
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

// =============================================================================
// Stat Card Component
// =============================================================================

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', color)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function ReferralsPage() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  // Fetch referrals data
  const { data, isLoading } = useQuery<ReferralsData>({
    queryKey: ['referrals'],
    queryFn: async () => {
      const response = await apiFetch('/api/referrals');
      if (!response.ok) throw new Error('Failed to fetch referrals');
      return response.json();
    },
  });

  // Fetch incoming referral (if user was referred)
  const { data: incoming } = useQuery<IncomingReferral>({
    queryKey: ['referrals', 'incoming'],
    queryFn: async () => {
      const response = await apiFetch('/api/referrals/incoming');
      if (!response.ok) throw new Error('Failed to fetch incoming referral');
      return response.json();
    },
  });

  // Claim reward mutation
  const claimRewardMutation = useMutation({
    mutationFn: async (rewardType: 'discount_25' | 'pro_upgrade') => {
      const response = await apiFetch('/api/referrals/claim-reward', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rewardType }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to claim reward');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['referrals'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to claim reward');
    },
  });

  // Claim referred reward mutation
  const claimReferredRewardMutation = useMutation({
    mutationFn: async (rewardType: 'discount_25' | 'pro_upgrade') => {
      const response = await apiFetch('/api/referrals/claim-referred-reward', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rewardType }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to claim reward');
      }
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['referrals', 'incoming'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to claim reward');
    },
  });

  const handleCopy = async () => {
    if (!data?.referralUrl) return;
    try {
      await navigator.clipboard.writeText(data.referralUrl);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const stats = data?.stats;
  const referrals = data?.referrals || [];
  const pendingRewards = stats?.rewardsPending || 0;

  return (
    <SettingsLayout>
      {/* Your Reward Banner (if you were referred and have an eligible reward) */}
      {incoming?.wasReferred && incoming?.rewardEligible && !incoming?.rewardClaimed && (
        <div className="rounded-lg border-2 border-purple-500/30 bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Gift className="h-6 w-6 text-purple-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-purple-700 mb-1">
                You have a referral reward!
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                You signed up through a referral link and are now eligible for a reward. Choose your
                reward below:
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => claimReferredRewardMutation.mutate('discount_25')}
                  disabled={claimReferredRewardMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {claimReferredRewardMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      25% Off Next Year
                    </>
                  )}
                </button>
                <button
                  onClick={() => claimReferredRewardMutation.mutate('pro_upgrade')}
                  disabled={claimReferredRewardMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {claimReferredRewardMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trophy className="h-4 w-4" />
                      Free Pro for 1 Year
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral URL Card */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Your Referral Link</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Share this link with friends. When they sign up and become paying customers, you both earn
          rewards!
        </p>

        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              readOnly
              value={data?.referralUrl || ''}
              className="w-full px-4 py-3 rounded-lg border bg-muted text-foreground font-mono text-sm"
            />
          </div>
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy
              </>
            )}
          </button>
        </div>

        <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-sm text-amber-700 font-medium">How it works:</p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1">
            <li>1. Share your link with friends</li>
            <li>2. They sign up and start a trial</li>
            <li>
              3. When they become paid subscribers (and pass 30-day window), you both earn rewards
            </li>
            <li>4. Choose: 25% off your next year or free Pro upgrade for 1 year</li>
          </ul>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          label="Link Clicks"
          value={stats?.totalClicks || 0}
          icon={MousePointer}
          color="bg-zinc-500/20 text-zinc-600"
        />
        <StatCard
          label="Signups"
          value={stats?.signups || 0}
          icon={UserPlus}
          color="bg-blue-500/20 text-blue-600"
        />
        <StatCard
          label="Trials Started"
          value={stats?.trialsStarted || 0}
          icon={Clock}
          color="bg-amber-500/20 text-amber-600"
        />
        <StatCard
          label="Converted"
          value={stats?.conversions || 0}
          icon={CreditCard}
          color="bg-green-500/20 text-green-600"
        />
        <StatCard
          label="Rewards Earned"
          value={stats?.rewardsEarned || 0}
          icon={Trophy}
          color="bg-emerald-500/20 text-emerald-600"
        />
        <StatCard
          label="Pending"
          value={stats?.rewardsPending || 0}
          icon={Gift}
          color="bg-purple-500/20 text-purple-600"
        />
      </div>

      {/* Pending Rewards Section */}
      {pendingRewards > 0 && (
        <div className="rounded-lg border-2 border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <Gift className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-700 mb-1">
                You have {pendingRewards} reward{pendingRewards > 1 ? 's' : ''} to claim!
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Your referrals have converted and passed the 30-day window. Choose your reward:
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => claimRewardMutation.mutate('discount_25')}
                  disabled={claimRewardMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {claimRewardMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      25% Off Next Year
                    </>
                  )}
                </button>
                <button
                  onClick={() => claimRewardMutation.mutate('pro_upgrade')}
                  disabled={claimRewardMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {claimRewardMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trophy className="h-4 w-4" />
                      Free Pro for 1 Year
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referrals List */}
      <div className="rounded-lg border bg-card">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Your Referrals</h2>
          <p className="text-sm text-muted-foreground">
            Track the progress of people you&apos;ve referred
          </p>
        </div>

        {referrals.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No referrals yet</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Share your referral link to start earning rewards. When your friends sign up and
              become paying customers, you&apos;ll both receive benefits!
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {referrals.map((referral) => (
              <div key={referral.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <Users className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">{referral.referredEmail}</p>
                    <p className="text-sm text-muted-foreground">
                      {referral.signedUpAt
                        ? `Signed up ${formatDate(new Date(referral.signedUpAt))}`
                        : referral.clickedAt
                          ? `Clicked ${formatDate(new Date(referral.clickedAt))}`
                          : 'Pending'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={referral.status} />
                  {referral.referrerRewardAppliedAt && (
                    <span className="text-xs text-muted-foreground">
                      {referral.referrerRewardType === 'discount_25'
                        ? '25% discount'
                        : 'Pro upgrade'}{' '}
                      claimed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share More Section */}
      <div className="rounded-lg border bg-card p-6 text-center">
        <h3 className="text-lg font-semibold mb-2">Keep Sharing, Keep Earning</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
          There&apos;s no limit to how many rewards you can earn! Keep sharing your referral link to
          help your friends discover Armenian Link.
        </p>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold transition-colors"
        >
          <Copy className="h-5 w-5" />
          Copy Referral Link
        </button>
      </div>
    </SettingsLayout>
  );
}
